import { apiGet, apiPost, apiPut, apiDelete } from './client';
import type { ApiResponse, PagedResponse, SearchParams } from './types';

// --- Opportunity Document (matches backend OpportunityDocument : BaseDocument) ---

export interface Opportunity {
  id: string;
  tenant_id: string;
  name: string;
  description: string;
  customer_id: string;
  contact_id: string;
  pipeline_id: string;
  stage_id: string;
  amount: number;
  currency: string;
  close_date: string | null;
  probability: number;
  owner_id: string;
  source: string;
  status: string;
  notes: string;
  tags: string[];
  branch_id: string;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
}

// --- Request DTOs (match backend CreateOpportunityRequest / UpdateOpportunityRequest) ---

export interface CreateOpportunityRequest {
  name: string;
  description?: string;
  customer_id: string;
  contact_id?: string;
  pipeline_id: string;
  stage_id: string;
  amount?: number;
  currency?: string;
  close_date?: string | null;
  probability?: number;
  owner_id?: string;
  source?: string;
  notes?: string;
  tags?: string[];
}

export interface UpdateOpportunityRequest {
  name: string;
  description?: string;
  customer_id: string;
  contact_id?: string;
  pipeline_id: string;
  stage_id: string;
  amount?: number;
  currency?: string;
  close_date?: string | null;
  probability?: number;
  owner_id?: string;
  source?: string;
  status?: string;
  notes?: string;
  tags?: string[];
}

// --- Filter params for opportunity list ---

export interface OpportunityFilters {
  stage_id?: string;
  pipeline_id?: string;
  owner_id?: string;
  customer_id?: string;
  status?: string;
  source?: string;
}

// --- API functions ---

const BASE = '/opportunities';

export async function listOpportunities(
  params: SearchParams,
  filters?: OpportunityFilters
): Promise<PagedResponse<Opportunity>> {
  const query: Record<string, unknown> = {
    from: params.from ?? 0,
    size: params.size ?? 20,
  };
  if (params.keyword) query.keyword = params.keyword;
  if (params.sort_field) query.sort_field = params.sort_field;
  if (params.sort_descending !== undefined) query.sort_descending = params.sort_descending;
  if (filters?.stage_id) query.stage_id = filters.stage_id;
  if (filters?.pipeline_id) query.pipeline_id = filters.pipeline_id;
  if (filters?.owner_id) query.owner_id = filters.owner_id;
  if (filters?.customer_id) query.customer_id = filters.customer_id;
  if (filters?.status) query.status = filters.status;
  if (filters?.source) query.source = filters.source;

  const response = await apiGet<PagedResponse<Opportunity>>(BASE, query);
  if (!response.data) {
    return { items: [], total: 0, page: 1, page_size: 20, total_pages: 0 };
  }
  return response.data;
}

export async function getOpportunity(id: string): Promise<Opportunity> {
  const response = await apiGet<Opportunity>(`${BASE}/${id}`);
  if (!response.data) {
    throw new Error(response.message || 'Opportunity not found');
  }
  return response.data;
}

export async function createOpportunity(data: CreateOpportunityRequest): Promise<Opportunity> {
  const response = await apiPost<Opportunity>(BASE, data);
  if (!response.data) {
    throw new Error(response.message || 'Failed to create opportunity');
  }
  return response.data;
}

export async function updateOpportunity(id: string, data: UpdateOpportunityRequest): Promise<Opportunity> {
  const response = await apiPut<Opportunity>(`${BASE}/${id}`, data);
  if (!response.data) {
    throw new Error(response.message || 'Failed to update opportunity');
  }
  return response.data;
}

export async function deleteOpportunity(id: string): Promise<ApiResponse<{ id: string }>> {
  return apiDelete<{ id: string }>(`${BASE}/${id}`);
}
